import React, { useState, useEffect, useRef } from 'react'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'
import { useTranslation } from 'react-i18next'
import { useHistory, useLocation } from 'react-router-dom'
import ViewFaultDetails from './ViewFaultDetails'
import LoadingSpinner from '../common/LoadingSpinner'
import EmptyPageResults from '../common/EmptyPageResults'
import SimpleButton from '../common/SimpleButton'
import GenericTable from '../common/GenericTable'
import ExpandableText from '../common/ExpandableText'
import SearchWithFilter from '../common/SearchWithFilter'
import FiltersModal, { FilterType } from '../common/FiltersModal'
import ActionsMenu from '../common/ActionsMenu'
import { toast } from '../common/Toast'
import { getCycleFaultProducts, getCyclesSummaryTotalCF, getCyclesSummaryTotalCycles, getCyclesSummaryTotalPending } from '../../utils/requests/cyclesAPI'
import { useWindowSize } from '../../libs/hooks'
import { useDeepCompareEffect } from '../../hooks/useDeepCompareEffect'
import {
  MenuKebabVerticalBackgroundIcon,
  MenuKebabVerticalIcon,
  PendingIcon,
  FlagAUIcon,
  FlagCAIcon,
  FlagCNIcon,
  FlagDEIcon,
  FlagUSIcon,
  FlagJPIcon,
  NotesIcon,
  InProgressIcon,
  ClosedIcon,
} from '../../icons'
import { currentDay, convertToSimpleDateWithFullTime, getOneMonthDateRange } from '../../utils/filters/date'
import { getImageByModel } from '../../utils/functions'
import { setBackNavigationState, clearBackNavigationState } from '../../actions/backNavigationState'
import * as ExternalServiceImages from '../../images/externalServices'
import './cycle-faults.scss'

const CycleFaults = (props) => {
  const { t } = useTranslation()
  const history = useHistory()
  const location = useLocation()
  const isMobile = useWindowSize()[0] <= 768
  const [cycleFaults, setCycleFaults] = useState([])
  const [cycleFaultsTableData, setCycleFaultsTableData] = useState([])
  const [totalCycles, setTotalCycles] = useState({})
  const [totalCF, setTotalCF] = useState({})
  const [totalPendingCycles, setTotalPendingCycles] = useState({})
  const [selectedCycleFault, setSelectedCycleFault] = useState(null)
  const [shouldApplyFilters, setShouldApplyFilters] = useState(true)
  const [selectedFilterUnits, setSelectedFilterUnits] = useState([])
  const [selectedFilterCountries, setSelectedFilterCountries] = useState([])
  const [selectedFilterStatus, setSelectedFilterStatus] = useState([])
  const [selectedFilterOfficeIp, setSelectedFilterOfficeIp] = useState([])
  const [filterQuery, setFilterQuery] = useState(null)
  const [areTotalCyclesLoading, setAreTotalCyclesLoading] = useState(false)
  const [areTotalCFLoading, setAreTotalCFLoading] = useState(false)
  const [areTotalPendingLoading, setAreTotalPendingLoading] = useState(false)
  const [isTableLoading, setIsTableLoading] = useState(false)
  const [showFiltersModal, setShowFiltersModal] = useState(false)
  const [canLoadMore, setCanLoadMore] = useState(false)
  const [selectedPage, setSelectedPage] = useState(1)
  const [isFirstLoad, setIsFirstLoad] = useState(true)
  const [expandedCycleFault, setExpandedCycleFault] = useState(0)
  const [showActionsMenu, setShowActionsMenu] = useState(null)
  const [units, setUnits] = useState([])
  const statuses = ['pending', 'closed', 'in_progress']
  const countries = ['AU', 'CA', 'CN', 'DE', 'JP', 'US']
  const officeIpFilters = ['scican_office']
  const cycleFaultsLimit = 20
  const { startDate, endDate } = getOneMonthDateRange()
  const isRestoringRef = useRef(!!props.backNavigationState)

  useEffect(() => {
    if (props.productTypes && Object.values(props.productTypes).length > 0) {
      const products = Object.values(props.productTypes).map((product) => product.name)
      setUnits(products)
    }
  }, [props.productTypes])

  useEffect(() => {
    if (selectedPage && selectedPage > 1 && !props.backNavigationState && !isRestoringRef.current) {
      handleLoadCycleFaults(false)
    }
  }, [selectedPage])

  useDeepCompareEffect(() => {
    let changeValueTimeout = window.setTimeout(
      () => {
        if (shouldApplyFilters && !props.backNavigationState) {
          setSelectedPage(1)
          handleLoadCycleFaults(true)
        }

        setShouldApplyFilters(true)
      },
      filterQuery ? 1000 : 0,
    )
    return () => {
      clearTimeout(changeValueTimeout)
    }
  }, [filterQuery, selectedFilterUnits, selectedFilterCountries, selectedFilterStatus, selectedFilterOfficeIp])

  useEffect(() => {
    setIsTableLoading(props.isLoading)
  }, [props.isLoading])

  useEffect(() => {
    mapTableData(cycleFaults)

    if (selectedCycleFault && !props.backNavigationState) {
      const selectedFault = cycleFaults?.find(
        (fault) => fault.cycle_fault === selectedCycleFault.cycle_fault && fault.serial_number === selectedCycleFault.serial_number,
      )
      setSelectedCycleFault(selectedFault)
    }
  }, [cycleFaults])

  useEffect(() => {
    let isActive = true
    const run = async () => {
      if (!props.backNavigationState) return
      const backNavState = props.backNavigationState
      props.actions.clearBackNavigationState()
      setShouldApplyFilters(false)
      setTimeout(() => {
        isRestoringRef.current = false
      }, 0)
      setIsTableLoading(true)
      setAreTotalCyclesLoading(true)
      setAreTotalCFLoading(true)
      setAreTotalPendingLoading(true)
      const backNavigationFilterQuery = backNavState.filterQuery
      const backNavigationSelectedFilterUnits = backNavState.selectedFilterUnits
      const backNavigationSelectedFilterCountries = backNavState.selectedFilterCountries
      const backNavigationSelectedFilterStatus = backNavState.selectedFilterStatus
      const backNavigationSelectedFilterOfficeIp = backNavState.selectedFilterOfficeIp
      const backNavigationSelectedPage = backNavState.selectedPage
      const selectionLimit = cycleFaultsLimit * backNavigationSelectedPage
      setFilterQuery(backNavigationFilterQuery)
      setSelectedFilterUnits(backNavigationSelectedFilterUnits)
      setSelectedFilterCountries(backNavigationSelectedFilterCountries)
      setSelectedFilterStatus(backNavigationSelectedFilterStatus)
      setSelectedFilterOfficeIp(backNavigationSelectedFilterOfficeIp)
      setSelectedPage(backNavigationSelectedPage)
      let queryParams = {
        queue: 1,
        page: 1,
        limit: selectionLimit,
      }
      let cyclesSummaryQueryParams = {}
      if (backNavigationFilterQuery) {
        queryParams = { ...queryParams, sn: backNavigationFilterQuery }
        cyclesSummaryQueryParams = { ...cyclesSummaryQueryParams, sn: backNavigationFilterQuery }
      } else {
        queryParams = {
          ...queryParams,
          start_date: startDate,
          end_date: endDate,
        }
      }
      if (backNavigationSelectedFilterCountries.length > 0) {
        const v = backNavigationSelectedFilterCountries.join(',')
        queryParams = { ...queryParams, country: v }
        cyclesSummaryQueryParams = { ...cyclesSummaryQueryParams, country: v }
      }
      if (backNavigationSelectedFilterUnits.length > 0) {
        const v = backNavigationSelectedFilterUnits.join(',')
        queryParams = { ...queryParams, product_type: v }
        cyclesSummaryQueryParams = { ...cyclesSummaryQueryParams, product_type: v }
      }
      if (backNavigationSelectedFilterStatus.length > 0) {
        const v = backNavigationSelectedFilterStatus.join(',')
        queryParams = { ...queryParams, status: v }
        cyclesSummaryQueryParams = { ...cyclesSummaryQueryParams, status: v }
      }
      if (backNavigationSelectedFilterOfficeIp.length > 0) {
        queryParams = { ...queryParams, office_ip_only: true }
        cyclesSummaryQueryParams = { ...cyclesSummaryQueryParams, office_ip_only: true }
      }
      let requestsErrored = 0
      const totalRequests = 4
      const handleAllErroredToast = () => {
        if (requestsErrored === totalRequests) toast.error(t('error.error_loading_data'))
      }
      // Fire all 4 calls (same as before), but don’t update state if effect is no longer active.
      getCyclesSummaryTotalCycles(cyclesSummaryQueryParams)
        .then((response) => {
          if (!isActive) return
          setTotalCycles(response.data?.total_cycles || {})
          setAreTotalCyclesLoading(false)
        })
        .catch((error) => {
          console.log('Error fetching cycle summary total cycles', error)
          requestsErrored++
          if (!isActive) return
          setAreTotalCyclesLoading(false)
          handleAllErroredToast()
        })
      getCyclesSummaryTotalCF(cyclesSummaryQueryParams)
        .then((response) => {
          if (!isActive) return
          setTotalCF(response.data?.total_cf || {})
          setAreTotalCFLoading(false)
        })
        .catch((error) => {
          console.log('Error fetching cycle summary cycle faults', error)
          requestsErrored++
          if (!isActive) return
          setAreTotalCFLoading(false)
          handleAllErroredToast()
        })
      getCyclesSummaryTotalPending(cyclesSummaryQueryParams)
        .then((response) => {
          if (!isActive) return
          setTotalPendingCycles(response.data?.total_cf_pending || {})
          setAreTotalPendingLoading(false)
        })
        .catch((error) => {
          console.log('Error fetching cycle summary total pending', error)
          requestsErrored++
          if (!isActive) return
          setAreTotalPendingLoading(false)
          handleAllErroredToast()
        })
      getCycleFaultProducts(queryParams)
        .then((response) => {
          if (!isActive) return
          const faults = response.data
          setCycleFaults(faults)
          setCanLoadMore(response.nextPage)
          if (backNavState.cycleFault && backNavState.serialNumber) {
            const selectedFault = faults.find((fault) => fault.cycle_fault === backNavState.cycleFault && fault.serial_number === backNavState.serialNumber)
            setSelectedCycleFault(selectedFault)
          }
          setIsTableLoading(false)
          setIsFirstLoad(false)
        })
        .catch((error) => {
          console.log('Error fetching cycle faults', error)
          requestsErrored++
          if (!isActive) return
          setIsTableLoading(false)
          setIsFirstLoad(false)
          handleAllErroredToast()
        })
    }
    run()
    return () => {
      isActive = false
    }
  }, [])

  const handleLoadCycleFaults = async (withReset) => {
    let queryParams = {
      queue: 1,
      page: withReset ? 1 : selectedPage,
      limit: cycleFaultsLimit,
    }

    let cyclesSummaryQueryParams = {}

    if (filterQuery) {
      queryParams = { ...queryParams, sn: filterQuery }
      cyclesSummaryQueryParams = { ...cyclesSummaryQueryParams, sn: filterQuery }
    } else {
      queryParams = {
        ...queryParams,
        start_date: startDate,
        end_date: endDate,
      }
    }

    if (selectedFilterCountries.length > 0) {
      queryParams = { ...queryParams, country: selectedFilterCountries.join(',') }
      cyclesSummaryQueryParams = { ...cyclesSummaryQueryParams, country: selectedFilterCountries.join(',') }
    }

    if (selectedFilterUnits.length > 0) {
      queryParams = { ...queryParams, product_type: selectedFilterUnits.join(',') }
      cyclesSummaryQueryParams = { ...cyclesSummaryQueryParams, product_type: selectedFilterUnits.join(',') }
    }

    if (selectedFilterStatus.length > 0) {
      queryParams = { ...queryParams, status: selectedFilterStatus.join(',') }
      cyclesSummaryQueryParams = { ...cyclesSummaryQueryParams, status: selectedFilterStatus.join(',') }
    }

    if (selectedFilterOfficeIp.length > 0) {
      queryParams = { ...queryParams, office_ip_only: true }
      cyclesSummaryQueryParams = { ...cyclesSummaryQueryParams, office_ip_only: true }
    }

    let requestsErrored = 0
    let totalRequests = 1
    
    if(!withReset) {
      setIsTableLoading(true)
    }

    if (!withReset) {
      setIsTableLoading(true)
    }

    if (withReset) {
      totalRequests = 4
      
      setAreTotalCyclesLoading(true)
      setAreTotalCFLoading(true)
      setAreTotalPendingLoading(true)
      setIsTableLoading(true)

      setAreTotalCyclesLoading(true)
      setAreTotalCFLoading(true)
      setAreTotalPendingLoading(true)
      setIsTableLoading(true)

      getCyclesSummaryTotalCycles(cyclesSummaryQueryParams)
        .then((response) => {
          setTotalCycles(response.data?.total_cycles || {})

          setAreTotalCyclesLoading(false)
        })
        .catch((error) => {
          console.log('Error fetching cycle summary total cyles', error)
          requestsErrored++

          setAreTotalCyclesLoading(false)

          if (requestsErrored === totalRequests) {
            toast.error(t('error.error_loading_data'))
          }
        })

      getCyclesSummaryTotalCF(cyclesSummaryQueryParams)
        .then((response) => {
          setTotalCF(response.data?.total_cf || {})

          setAreTotalCFLoading(false)
        })
        .catch((error) => {
          console.log('Error fetching cycle summary total cycle faults', error)
          requestsErrored++

          setAreTotalCFLoading(false)

          if (requestsErrored === totalRequests) {
            toast.error(t('error.error_loading_data'))
          }
        })

      getCyclesSummaryTotalPending(cyclesSummaryQueryParams)
        .then((response) => {
          setTotalPendingCycles(response.data?.total_cf_pending || {})

          setAreTotalPendingLoading(false)
        })
        .catch((error) => {
          console.log('Error fetching cycle summary pending action', error)
          requestsErrored++

          setAreTotalPendingLoading(false)

          if (requestsErrored === totalRequests) {
            toast.error(t('error.error_loading_data'))
          }
        })
    }

    getCycleFaultProducts(queryParams)
      .then((response) => {
        const faults = withReset ? response.data : [...cycleFaults, ...response.data]
        setCycleFaults(faults)

        setCanLoadMore(response.nextPage)

        setIsTableLoading(false)
        setIsFirstLoad(false)
      })
      .catch((error) => {
        console.log('Error fetching cycle faults', error)
        requestsErrored++

        setIsTableLoading(false)
        setIsFirstLoad(false)

        if (requestsErrored === totalRequests) {
          toast.error(t('error.error_loading_data'))
        }
      })
  }

  const mapTableData = (data) => {
    const tableData = data?.map((cycleFault, index) => {
      return {
        index,
        identifier: `${cycleFault.cycle_fault}-${cycleFault.serial_number}`,
        product: cycleFault.model,
        sn: cycleFault.serial_number,
        queue: index + 1,
        country: cycleFault.country,
        cf: `CF ${cycleFault.cycle_fault}`,
        cf_date: convertToSimpleDateWithFullTime(cycleFault.date_originated),
        externalService: cycleFault.external_service?.registered === 1 && cycleFault.external_service?.name,
        note: cycleFault.notes?.length > 0 || false,
        status: cycleFault.action_in_progress ? 'in_progress' : cycleFault.pending_for_action ? 'pending' : 'closed',
        actions: [
          {
            label: 'view',
            onActionClick: (e) => {
              e.stopPropagation()
              openCycleFaultDetails(`${cycleFault.cycle_fault}-${cycleFault.serial_number}`, data)
            },
          },
          {
            label: 'monitoring.product_page',
            onActionClick: (e) => {
              e.stopPropagation()
              navigateToProductPage(cycleFault.serial_number, cycleFault.model_id)
            },
          },
          {
            label: 'monitoring.view_all_notes',
            onActionClick: (e) => {
              e.stopPropagation()
              navigateToAllNotes(cycleFault.serial_number, cycleFault.model_id)
            },
          },
        ],
      }
    })

    setCycleFaultsTableData(tableData)
  }

  const handleSearch = (value) => {
    if (value?.length > 5) {
      setIsTableLoading(true)
      setFilterQuery(value)
    } else if (value?.length === 0 && filterQuery?.length > 0) {
      setIsTableLoading(true)
      setFilterQuery(null)
    }
  }

  const handleLoadMore = () => {
    setSelectedPage((prevValue) => (prevValue ? prevValue + 1 : 2))
  }

  const handleResetFilters = () => {
    setSelectedFilterUnits([])
    setSelectedFilterCountries([])
    setSelectedFilterStatus([])
    setSelectedFilterOfficeIp([])
  }

  const openCycleFaultDetails = (identifier, data) => {
    const [cycleFault, serialNumber] = identifier.split('-')

    const selectedFault = data?.find((fault) => fault.cycle_fault === cycleFault && fault.serial_number === serialNumber)

    setSelectedCycleFault(selectedFault)
  }

  const navigateToProductPage = (serialNumber, modelId) => {
    handleTableNavigationWithState()

    history.push({
      pathname: `/products/${serialNumber}`,
      state: {
        model_id: modelId,
        withBackNavigation: true,
      },
    })
  }

  const navigateToAllNotes = (serialNumber, modelId) => {
    handleTableNavigationWithState()

    const items = [{ model_id: modelId, serial_number: serialNumber }]

    history.push({
      pathname: `/products/${serialNumber}/notes`,
      state: {
        items,
        withBackNavigation: true,
      },
    })
  }

  const renderCycleFaultCountry = (country) => {
    switch (country) {
      case 'AU':
        return (
          <div className="country-wrapper">
            <FlagAUIcon />
            <p>{t('filter_context.AU')}</p>
          </div>
        )
      case 'CA':
        return (
          <div className="country-wrapper">
            <FlagCAIcon />
            <p>{t('filter_context.CA')}</p>
          </div>
        )
      case 'CN':
        return (
          <div className="country-wrapper">
            <FlagCNIcon />
            <p>{t('filter_context.CN')}</p>
          </div>
        )
      case 'DE':
        return (
          <div className="country-wrapper">
            <FlagDEIcon />
            <p>{t('filter_context.DE')}</p>
          </div>
        )
      case 'JP':
        return (
          <div className="country-wrapper">
            <FlagJPIcon />
            <p>{t('filter_context.JP')}</p>
          </div>
        )
      case 'US':
        return (
          <div className="country-wrapper">
            <FlagUSIcon />
            <p>{t('filter_context.US')}</p>
          </div>
        )
      default:
        return null
    }
  }

  const renderCycleFaultStatus = (status) => {
    switch (status) {
      case 'in_progress':
        return (
          <div className="status-in-progress">
            <InProgressIcon />
            <p>{t('in_progress')}</p>
          </div>
        )
      case 'pending':
        return (
          <div className="status-pending">
            <PendingIcon />
            <p>{t('pending')}</p>
          </div>
        )
      case 'closed':
        return (
          <div className="status-inactive">
            <ClosedIcon />
            <p>{t('closed')}</p>
          </div>
        )
      default:
        return null
    }
  }

  const getExternalServiceLogo = (externalService) => {
    if (!externalService) {
      return ''
    }

    const mappedService = `${externalService.toLowerCase().replace(' ', '_')}_external_service`
    return ExternalServiceImages[mappedService]
  }

  const handleUpdateCycleFault = (updatedCycleFault) => {
    const updatedCycleFaultStatus = updatedCycleFault.pending_for_action ? 'pending' : updatedCycleFault.action_in_progress ? 'in_progress' : 'closed'

    const currentStatusNotMatchingFilters = selectedFilterStatus.length > 0 && !selectedFilterStatus.includes(updatedCycleFaultStatus)
    const currentStatusClosedAndNoSNSearch = updatedCycleFaultStatus === 'closed' && !filterQuery && selectedFilterStatus.length === 0

    if (currentStatusNotMatchingFilters || currentStatusClosedAndNoSNSearch) {
      const updatedCycleFaults = cycleFaults.filter((cycleFault) => cycleFault.id !== updatedCycleFault.id)
      setCycleFaults(updatedCycleFaults)
      return
    }

    const updatedCycleFaults = cycleFaults.map((cycleFault) => {
      if (cycleFault.id === updatedCycleFault.id) {
        return updatedCycleFault
      }

      return cycleFault
    })

    setCycleFaults(updatedCycleFaults)
  }

  const handleDetailsNavigationWithState = () => {
    props.actions.setBackNavigationState({
      backNavPage: location.pathname,
      serialNumber: selectedCycleFault?.serial_number,
      cycleFault: selectedCycleFault?.cycle_fault,
      filterQuery: filterQuery,
      selectedFilterUnits: selectedFilterUnits,
      selectedFilterCountries: selectedFilterCountries,
      selectedFilterStatus: selectedFilterStatus,
      selectedFilterOfficeIp: selectedFilterOfficeIp,
      selectedPage: selectedPage,
    })
  }

  const handleTableNavigationWithState = () => {
    props.actions.setBackNavigationState({
      backNavPage: location.pathname,
      filterQuery: filterQuery,
      selectedFilterUnits: selectedFilterUnits,
      selectedFilterCountries: selectedFilterCountries,
      selectedFilterStatus: selectedFilterStatus,
      selectedFilterOfficeIp: selectedFilterOfficeIp,
      selectedPage: selectedPage,
    })
  }

  const toggleShowActionsMenu = (index) => {
    setShowActionsMenu(showActionsMenu === index ? null : index)
  }

  return (
    <div className="cycle-faults-content-wrapper">
      <div className="actions-wrapper">
        <SearchWithFilter
          searchText={filterQuery}
          placeholder={t('device_context.search_by_serial_number')}
          onSearch={(e) => handleSearch(e)}
          showFiltersModal={() => setShowFiltersModal(true)}
          onResetFilters={() => handleResetFilters()}
          showCounterBadge={
            selectedFilterUnits.length > 0 || selectedFilterCountries.length > 0 || selectedFilterStatus.length > 0 || selectedFilterOfficeIp.length > 0
          }
          counterBadge={selectedFilterUnits.length + selectedFilterCountries.length + selectedFilterStatus.length + selectedFilterOfficeIp.length}
        />
        <p className="current-date">
          <span>{currentDay().split(',')[0]},</span>
          <span className="medium">
            {currentDay().split(',')[1]},{currentDay().split(',')[2]}
          </span>
        </p>
      </div>
      <div className="total-cycles-wrapper">
        <div className="cycles-card">
          <div className="card-header-wrapper">
            <div className="title">{t('cycles.labels.total-cycles')}</div>
            {areTotalCyclesLoading && (
              <div className="internal-spinner-wrapper">
                <LoadingSpinner />
              </div>
            )}
          </div>
          <div className="data-wrapper">
            <div className="data-item">
              <div className="value">{totalCycles.today || 0}</div>
              <div className="label">{t('today')}</div>
            </div>
            <div className="data-item">
              <div className="value">{totalCycles.last_seven_days || 0}</div>
              <div className="label">{t('cycles.card.last_week')}</div>
            </div>
            <div className="data-item">
              <div className="value">{totalCycles.last_thirty_days || 0}</div>
              <div className="label">{t('cycles.card.last_month')}</div>
            </div>
          </div>
        </div>
        <div className="cycles-card">
          <div className="card-header-wrapper">
            <div className="title">{t('cycles.labels.total-cf')}</div>
            {areTotalCFLoading && (
              <div className="internal-spinner-wrapper">
                <LoadingSpinner />
              </div>
            )}
          </div>
          <div className="data-wrapper">
            <div className="data-item">
              <div className="value">{totalCF.today || 0}</div>
              <div className="label">{t('today')}</div>
            </div>
            <div className="data-item">
              <div className="value">{totalCF.last_seven_days || 0}</div>
              <div className="label">{t('cycles.card.last_week')}</div>
            </div>
            <div className="data-item">
              <div className="value">{totalCF.last_thirty_days || 0}</div>
              <div className="label">{t('cycles.card.last_month')}</div>
            </div>
          </div>
        </div>
        <div className="cycles-card">
          <div className="card-header-wrapper">
            <div className="title">{t('cycles.labels.total-cf-pending')}</div>
            {areTotalPendingLoading && (
              <div className="internal-spinner-wrapper">
                <LoadingSpinner />
              </div>
            )}
          </div>
          <div className="data-wrapper">
            <div className="data-item">
              <div className="value">{totalPendingCycles.today || 0}</div>
              <div className="label">{t('today')}</div>
            </div>
            <div className="data-item">
              <div className="value">{totalPendingCycles.last_seven_days || 0}</div>
              <div className="label">{t('cycles.card.last_week')}</div>
            </div>
            <div className="data-item">
              <div className="value">{totalPendingCycles.last_thirty_days || 0}</div>
              <div className="label">{t('cycles.card.last_month')}</div>
            </div>
          </div>
        </div>
      </div>
      {!isMobile && cycleFaultsTableData.length > 0 && (
        <GenericTable
          data={cycleFaultsTableData}
          headers={[
            {
              title: t('product'),
            },
            {
              title: t('serial_number'),
            },
            {
              title: t('monitoring.queue'),
            },
            {
              title: t('country'),
            },
            {
              title: t('cf'),
            },
            {
              title: t('monitoring.cf_date'),
            },
            {
              title: t('monitoring.note'),
            },
            {
              title: t('status'),
            },
            {
              title: '',
            },
          ]}
          keys={['product', 'sn', 'queue', 'country', 'cf', 'cf_date', 'note', 'status', 'action']}
          keyRenderer={{
            product: (item) => {
              return (
                <div className="product-wrapper">
                  <div className="product-image">
                    <img src={getImageByModel(item.product)} alt={item.product} />
                  </div>
                  <ExpandableText text={item.product} />
                </div>
              )
            },
            sn: (item) => {
              return (
                <div className="serial-number-wrapper">
                  <ExpandableText text={item.sn} />
                </div>
              )
            },
            queue: (item, index) => {
              return <>{`${index + 1}`}</>
            },
            country: (item) => {
              return <>{renderCycleFaultCountry(item.country)}</>
            },
            cf: (item) => {
              return <div className="cf-wrapper">{item.cf}</div>
            },
            cf_date: (item) => {
              return (
                <div className="cf-date-wrapper">
                  <ExpandableText text={item.cf_date} />
                </div>
              )
            },
            note: (item) => {
              return <>{item.note && <NotesIcon />}</>
            },
            status: (item) => {
              return (
                <>
                  {item.externalService ? (
                    <img className="external-service-logo" src={getExternalServiceLogo(item.externalService)} alt="external_service" />
                  ) : (
                    <div className="status-wrapper">{renderCycleFaultStatus(item.status)}</div>
                  )}
                </>
              )
            },
            action: (item) => {
              return (
                <div
                  className="table-actions-wrapper"
                  onClick={(e) => {
                    e.stopPropagation()
                    toggleShowActionsMenu(item['index'])
                  }}
                >
                  {showActionsMenu === item['index'] ? <MenuKebabVerticalBackgroundIcon /> : <MenuKebabVerticalIcon />}
                  {showActionsMenu === item['index'] && (
                    <ActionsMenu setHideMenu={() => setShowActionsMenu(null)} actions={item.actions} isLoading={isTableLoading} />
                  )}
                </div>
              )
            },
          }}
          onRowClick={(cycleFault) => openCycleFaultDetails(cycleFault.identifier, cycleFaults)}
        />
      )}
      {isMobile &&
        cycleFaultsTableData.map((cycleFault, index) => (
          <div key={index} className="card" onClick={() => setExpandedCycleFault(index)}>
            <div className={'card-item' + (expandedCycleFault !== index ? ' align-center' : '')}>
              <div className="card-item-title">{t('product')}</div>
              <div className={'card-item-body' + (expandedCycleFault !== index ? ' align-center' : '')}>
                <div className="card-description">
                  <div className="image-wrapper">
                    <img src={getImageByModel(cycleFault.product)} alt="product" />
                  </div>
                  {cycleFault.product}
                </div>
                <div
                  className="card-actions"
                  onClick={(e) => {
                    e.stopPropagation()
                    setShowActionsMenu(index)
                  }}
                >
                  {showActionsMenu === index ? <MenuKebabVerticalBackgroundIcon /> : <MenuKebabVerticalIcon />}
                  {showActionsMenu === index && (
                    <ActionsMenu setHideMenu={() => setShowActionsMenu(null)} actions={cycleFault.actions} isLoading={isTableLoading} />
                  )}
                </div>
              </div>
            </div>
            {expandedCycleFault === index && (
              <>
                <div className="card-item">
                  <div className="card-item-title">{t('serial_number')}</div>
                  <div className="card-item-body">{cycleFault.sn}</div>
                </div>
                <div className="card-item">
                  <div className="card-item-title">{t('monitoring.queue')}</div>
                  <div className="card-item-body">{cycleFault.queue}</div>
                </div>
                <div className="card-item">
                  <div className="card-item-title">{t('country')}</div>
                  <div className="card-item-body">{renderCycleFaultCountry(cycleFault.country)}</div>
                </div>
                <div className="card-item">
                  <div className="card-item-title">{t('cf')}</div>
                  <div className="card-item-body color-blue">{cycleFault.cf}</div>
                </div>
                <div className="card-item">
                  <div className="card-item-title">{t('monitoring.cf_date_short')}</div>
                  <div className="card-item-body">{cycleFault.cf_date}</div>
                </div>
                <div className="card-item">
                  <div className="card-item-title">{t('monitoring.note')}</div>
                  <div className="card-item-body">{cycleFault.note && <NotesIcon />}</div>
                </div>
                <div className="card-item">
                  <div className="card-item-title">{t('status')}</div>
                  <div className="card-item-body">
                    <div className="card-item-body-status">
                      {cycleFault.externalService ? (
                        <img className="external-service-logo" src={getExternalServiceLogo(cycleFault.externalService)} alt="external_service" />
                      ) : (
                        <>{renderCycleFaultStatus(cycleFault.status)}</>
                      )}
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        ))}
      {!isTableLoading && !isFirstLoad && cycleFaultsTableData.length === 0 && (
        <EmptyPageResults title={t('monitoring.no-cycle-faults-found')} subtitle={t('monitoring.no-cycle-faults-found-extra')} />
      )}
      {canLoadMore && (
        <div className="buttons-wrapper">
          <SimpleButton className="load-more-button" onClick={() => handleLoadMore()}>
            {t('load_more')}
          </SimpleButton>
        </div>
      )}
      {selectedCycleFault && (
        <ViewFaultDetails
          selectedCycleFault={selectedCycleFault}
          officeIpOnly={selectedFilterOfficeIp.length > 0}
          handleUpdateCycleFault={(updatedCycleFault) => handleUpdateCycleFault(updatedCycleFault)}
          closeModal={() => {
            setSelectedCycleFault(null)
          }}
          showSpinner={() => setIsTableLoading(true)}
          hideSpinner={() => setIsTableLoading(false)}
          handleNavigationWithState={() => handleDetailsNavigationWithState()}
        />
      )}
      {showFiltersModal && (
        <FiltersModal
          filters={[
            {
              name: t('status'),
              type: FilterType.multiSelect,
              dataset: statuses,
              input: selectedFilterStatus,
              output: (filterStatuses) => {
                setSelectedFilterStatus(filterStatuses)
              },
            },
            {
              name: t('monitoring.unit'),
              type: FilterType.multiSelect,
              dataset: units,
              input: selectedFilterUnits,
              output: (filterUnits) => {
                setSelectedFilterUnits(filterUnits)
              },
            },
            {
              name: t('country'),
              type: FilterType.multiSelect,
              dataset: countries,
              input: selectedFilterCountries,
              output: (filterCountries) => {
                setSelectedFilterCountries(filterCountries)
              },
            },
            {
              name: t('ip'),
              type: FilterType.multiSelect,
              dataset: officeIpFilters,
              input: selectedFilterOfficeIp,
              output: (filterOfficeIp) => {
                setSelectedFilterOfficeIp(filterOfficeIp)
              },
            },
          ]}
          shouldFilter={(shouldApply) => setShouldApplyFilters(shouldApply)}
          resetFilters={() => handleResetFilters()}
          closeFilters={() => setShowFiltersModal(false)}
        />
      )}
      {isTableLoading && (
        <div className="spinner-wrapper">
          <LoadingSpinner />
        </div>
      )}
    </div>
  )
}

function stateToProps({ productTypes, backNavigationState }) {
  return {
    productTypes,
    backNavigationState,
  }
}

function dispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(
      {
        setBackNavigationState,
        clearBackNavigationState,
      },
      dispatch,
    ),
  }
}

export default connect(stateToProps, dispatchToProps)(CycleFaults)

